// SPDX-License-Identifier: GPL-3.0-or-later
// Rex, the rogue SigmaBot: the villain on the backboard (THEME.md section 5.2).
// His belly panel shows the command of the current mode as a stylized label.
// The label is never a runnable command line.

import { REX_COMMANDS, type RexCommandKey } from "../animation.ts";
import { drawCommandPanel } from "../art/labels.ts";
import { addBot, botFront } from "./bot.ts";
import {
  type Built,
  type CharacterEnv,
  disposeTree,
  makeLabel,
  motion,
  namedGroup,
  rad,
  sec,
} from "./common.ts";
import type { SigmaBotSpec } from "./sigmabot/index.ts";

/** The look of Rex: a tall cherry SigmaBot with an angry face, fangs and an antenna. */
export const REX_SPEC: SigmaBotSpec = {
  body: "tall",
  palette: "cherry",
  eyes: "angry",
  mouth: "fang",
  hat: "none",
  accessory: "antenna",
};

/** Size of one body unit of Rex, in recipe units. The tallest part (the antenna) is below 260. */
const SCALE = 2;

/** Rex tilts toward the lit shot for this time. */
export const REX_AIM_MS = 600;
const AIM_DEG = 10;

function isCommandKey(key: string): key is RexCommandKey {
  return Object.hasOwn(REX_COMMANDS, key);
}

/** Tilt of the body in radians: [pitch, roll] for an aim direction. */
export function aimAngles(dir: string): readonly [number, number] {
  if (dir === "left") return [0, rad(AIM_DEG)];
  if (dir === "right") return [0, -rad(AIM_DEG)];
  return [rad(AIM_DEG), 0];
}

export function buildRex(env: CharacterEnv): Built {
  const group = namedGroup("Rex");
  // The body part moves and tilts. The base stays at the group origin.
  const rig = namedGroup("RexRig");
  group.add(rig);

  addBot(rig, "RexBot", REX_SPEC, SCALE);

  let cmd: RexCommandKey = "standby";
  let cursorShown = true;
  const panel = makeLabel(env, 70, 50, 256, 192, (ctx, w, h) =>
    drawCommandPanel(ctx, w, h, REX_COMMANDS[cmd], true),
  );
  panel.mesh.position.set(0, 62, botFront(REX_SPEC, SCALE, 62) + 0.6);
  rig.add(panel.mesh);

  let aimStart = Number.NEGATIVE_INFINITY;
  let aimDir = "center";
  return {
    group,
    tick(timeMs) {
      const a = motion(env);
      rig.position.y = 4 * Math.sin(sec(timeMs) * 1.5) * a;
      const elapsed = timeMs - aimStart;
      if (a > 0 && elapsed >= 0 && elapsed < REX_AIM_MS) {
        const [pitch, roll] = aimAngles(aimDir);
        rig.rotation.x = pitch;
        rig.rotation.z = roll;
      } else {
        rig.rotation.x = 0;
        rig.rotation.z = 0;
      }
      // The cursor blinks at 1 Hz. With reduced motion it stays on.
      const on = a === 0 ? true : Math.floor(sec(timeMs) * 2) % 2 === 0;
      if (on !== cursorShown) {
        cursorShown = on;
        panel.redraw((ctx, w, h) => drawCommandPanel(ctx, w, h, REX_COMMANDS[cmd], cursorShown));
      }
    },
    play(name, arg, timeMs) {
      if (name === "cmd" && isCommandKey(arg) && arg !== cmd) {
        cmd = arg;
        panel.redraw((ctx, w, h) => drawCommandPanel(ctx, w, h, REX_COMMANDS[cmd], cursorShown));
      } else if (name === "aim") {
        aimStart = timeMs;
        aimDir = arg;
      }
    },
    dispose() {
      disposeTree(group);
    },
  };
}
